import {useEffect} from 'react';
import {useAppDispatch, useAppSelector} from './redux';
import socketService from '../middleware/websocketMiddleware';
import {selectIsAuth} from "../features/auth/model/selectors";
import {setMessage} from "../features/messages/model/slice";
import {MESSAGE_SEVERITY} from "../utils/const";
import {alertRaised, IAlertData} from "../features/notify/model/actions";
import {unlockAudioOnGesture} from "../features/notify/model/browser";
import {useOpenChannel} from "../features/notify/model/useOpenChannel";
import {usePermissions} from "./usePermissions";

/** Событие `notification` с бэка (NotificationService). */
interface INotificationPayload {
    type: string;
    text: string;
    created_at: string;
    data?: Record<string, unknown>;
}

/**
 * Оповещения в приложении: после входа подключает сокет, входит в комнаты
 * пользователя и компании (`notification:join`) и показывает текст каждого
 * события `notification` всплывающим сообщением. Алерт (S4) — ещё звук и
 * системное уведомление; клик по нему с любой страницы открывает машину на
 * доске (`useOpenChannel`, S4.1). Подключается один раз в App.
 * Без способности `notifications` (ROLES.md § 4) — только вход в комнаты (живая
 * доска и чат), без всплывающих, звука и системных (S20.2).
 */
export function useNotifications() {
    const dispatch = useAppDispatch();
    const isAuth = useAppSelector(selectIsAuth);
    const openChannel = useOpenChannel();
    const {notifications} = usePermissions();

    useEffect(() => {
        if (!isAuth || !notifications) return;
        return unlockAudioOnGesture();
    }, [isAuth, notifications]);

    useEffect(() => {
        if (!isAuth) return;
        const socket = socketService.connect();

        const handleConnect = () => {
            socket.emit('notification:join');
        };

        const handleNotification = (payload: INotificationPayload) => {
            dispatch(setMessage({severity: MESSAGE_SEVERITY.info, text: payload.text}));
            if (payload.type === 'alert') dispatch(alertRaised((payload.data ?? {}) as IAlertData, openChannel));
        };

        socket.on('connect', handleConnect);
        if (notifications) socket.on('notification', handleNotification);

        if (socket.connected) handleConnect();

        return () => {
            socket.off('connect', handleConnect);
            socket.off('notification', handleNotification);
        };
    }, [dispatch, isAuth, openChannel, notifications]);
}
